import React, { useRef, useEffect, useCallback, useState} from 'react';
import {
    setPullablePartDragging,
    setParameterSliderDown} from "actions/index.js";
import EffectParameterSlider from 'components/effects/EffectParameterSlider.js';
import Colors from 'components/Colors.js';
import EnvParams from './EnvParams.js';
import {connect} from 'react-redux';
import {SYNTH_ENVELOPE} from 'constants/contextual-help.js';
import Documented from 'components/help/Documented.js';
import {
    getValueForParameter,
    getPathPoints} from './getPathPoints.js';

const WIDTH = 180;
const HEIGHT = 60;
const colors = new Colors();

const GraphicalEnvelope = ({
    draggable=true,
    fullText,
    sequencerEngine,
    instrument,
    envelope,
    width=WIDTH,
    height=HEIGHT,
    name,
    hasRelease,
    hasLevel,
    backgroundColor,
    fillColor,
    lineColor,
    selectedParameter,
    setParameterSliderDown,
    setPullablePartDragging,
}) => { 
    let [parameters, setParameters] = useState(undefined);
    let [parameter, setParameter] = useState(undefined);
    let ref = useRef(undefined);
    let points = getPathPoints(envelope, width, height, hasRelease, hasLevel);

    useEffect(() => {
        if (selectedParameter === envelope.attackParameter ||
            selectedParameter === envelope.decayParameter ||
            selectedParameter === envelope.sustainParameter ||
            selectedParameter === envelope.releaseParameter) {
            setParameter(selectedParameter);
        }
    }, [setParameter, selectedParameter, envelope]);

    useEffect(() => {
        setParameterSliderDown(parameters);
    }, [parameters]);

    const onMouseMove = useCallback((e) => {
        if (!parameters) {
            return;
        }
        let rect = ref.current.getBoundingClientRect();
        let x = e.clientX - rect.left;
        let y = e.clientY - rect.top;
        parameters.forEach(
            parameter => {
                let val = getValueForParameter(parameter, envelope, x, y, width, height);
                parameter.set(val, false);
            });
    }, [parameters, width, height]);

    let style = {
    };
    if (backgroundColor) {
        style.backgroundColor = backgroundColor;
    }

    if (fillColor) {
        fillColor = colors.transparent(colors.saturate(fillColor, 1), 0.2);
    }

    return (
        <Documented
          draggable={draggable}
          onDragOver={(e) => e.preventDefault()}
          onDragStart={() => instrument ? setPullablePartDragging({
              type: "SYNTH_ENVELOPE",
              id: envelope.id,
              track: instrument.track,
              subType: name,
              instrument
          }) : setPullablePartDragging({
              type: "MODULE_ENVELOPE",
              id: envelope.id,
              subType: envelope.number,
              instrument: envelope
          }) }
          element={SYNTH_ENVELOPE}
          arg={name}
          className="graphical-envelope">
          <div className="env-toolbar">
            <div
              style={style}
              className="env-name">
              {name}
            </div>
            <EnvParams
              fullText={fullText}
              setParameter={setParameter}
              selectedParameter={parameter}
              envelope={envelope}
              hasLevel={hasLevel}
              hasRelease={hasRelease}/>
          </div>
          {parameter ? <EffectParameterSlider
                         name={parameter.name}
                         isParameter={true}
                         lfoEnabled={true}
                         sequencerEngine={sequencerEngine}
                         parameter={parameter}
                         min={parameter.min}
                         max={parameter.max}
                         step={0.001}
       /> : ''}
          <div
            draggable={true}
            onDragStart={(e) => e.preventDefault()}
            className="graphics-container">
            <svg
              ref={ref}
              style={style}
              onMouseMove={onMouseMove}
              onMouseUp={() => setParameters(undefined)}
              onMouseLeave={() => setParameters(undefined)}
              width={width} height={height}>
              {getLines(points, height, lineColor, fillColor)}
              {getPoints(points, envelope, parameters, setParameters, hasLevel)}
            </svg>
            <div className="env-slider">
            </div>
          </div>
        </Documented>);
}

const getPoints = (path, envelope, parameters, setParameters, hasLevel) => {
    let points = [];

    for (let i=2; i < path.length - 3; i += 2) {
        let params = i === 2 ?
            (hasLevel ? [envelope.attackParameter, envelope.levelParameter] : [envelope.attackParameter]) :
            i === 4 ?
            [envelope.decayParameter, envelope.sustainParameter] :
            i === 6 ?
            [envelope.sustainParameter, envelope.releaseParameter] :
            [envelope.releaseParameter];

        let x1 = path[i];
        let y1 = path[i + 1];
        let x2 = path[i + 2];
        let y2 = path[i + 3];
        let selected = false;
        if (parameters) {
            selected = parameters[0] === params[0];
        }
        points.push((
            <circle
              key={i}
              onMouseDown={(e) => {
                  e.stopPropagation();
                  setParameters(params);
              }}
              cx={x1}
              cy={y1}
              r={3}
              className={selected ? "selected point" : "point"}
              fill={"#2fffec"}
              strokeWidth={2}
            />
            
        ));
    }
    return points;
};

const getLines = (path, height, lineColor, fillColor) => {
    let lines = [];

    for (let i=0; i < path.length - 3; i += 2) {
        let x1 = path[i];
        let y1 = path[i + 1];
        let x2 = path[i + 2];
        let y2 = path[i + 3];
        lines.push((
            <polygon key={'poly-'+i} points={[x1, height, x1, y1, x2, y2, x2, height]} fill={fillColor || "red"} strokeWidth={1}/>
        ));
        lines.push((
            <line key={'line-' + i} x1={x1} y1={y1} x2={x2} y2={y2} strokeWidth={2} stroke={lineColor || "red"}/>
        ));
    }
    return lines;
}

const mapStateToProps = (state, {envelope}) => {
    return {
        attack: envelope.attackParameter.get(),
        decay: envelope.decayParameter.get(),
        sustain: envelope.sustainParameter.get(),
        release: envelope.releaseParameter.get(),
        level: envelope.levelParameter && envelope.levelParameter.get(),
        selectedParameter: state.effects.accumulatorParameterSelected
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        setParameterSliderDown: parameter => dispatch(setParameterSliderDown(parameter)),
        setPullablePartDragging: part => dispatch(setPullablePartDragging(part))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(GraphicalEnvelope);
